import React, { useMemo } from "react";
import { View, Text, Pressable, StyleSheet } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

type Props = {
  currentIndex: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  onResults: () => void;
  mode?: "top" | "bottom";
};

export default function DetailResultsNavigator({
  currentIndex,
  total,
  onPrev,
  onNext,
  onResults,
  mode = "bottom",
}: Props) {
  const insets = useSafeAreaInsets();

  const isFirst = currentIndex <= 0;
  const isLast = total === 0 || currentIndex >= total - 1;
  const isTop = mode === "top";

  const wrapBottom = useMemo(() => Math.max(insets.bottom, 10), [insets.bottom]);

  if (isTop) {
    return (
      <View style={styles.topWrap}>
        <View style={styles.topGroupLeft}>
          <Pressable
            onPress={onPrev}
            disabled={isFirst}
            style={({ pressed }) => [
              styles.topBtn,
              pressed && !isFirst && styles.pressed,
              isFirst && styles.disabled,
            ]}
          >
            <Text style={[styles.topBtnText, isFirst && styles.disabledText]}>
              Prev
            </Text>
          </Pressable>

          <View style={styles.topStatusBox}>
            <Text style={styles.topStatusText}>
              {total === 0 ? "0 of 0" : `${currentIndex + 1} of ${total}`}
            </Text>
          </View>
        </View>

        <View style={styles.topGroupRight}>
          <Pressable
            onPress={onResults}
            style={({ pressed }) => [
              styles.topResultsBtn,
              pressed && styles.pressed,
            ]}
          >
            <Text style={styles.topResultsText}>Results</Text>
          </Pressable>

          <Pressable
            onPress={onNext}
            disabled={isLast}
            style={({ pressed }) => [
              styles.topBtn,
              styles.topNextBtn,
              pressed && !isLast && styles.pressed,
              isLast && styles.disabled,
            ]}
          >
            <Text style={[styles.topBtnText, isLast && styles.disabledText]}>
              Next
            </Text>
          </Pressable>
        </View>
      </View>
    );
  }

  return (
    <View pointerEvents="box-none" style={[styles.bottomWrap, { bottom: wrapBottom }]}>
      <View style={styles.bottomBar}>
        <Pressable
          onPress={onPrev}
          disabled={isFirst}
          style={[styles.bottomBtn, isFirst && styles.bottomBtnDisabled]}
        >
          <Text style={[styles.bottomBtnText, isFirst && styles.bottomBtnTextDisabled]}>
            Prev
          </Text>
        </Pressable>

        <View style={styles.bottomStatusBox}>
          <Text style={styles.bottomStatusText}>
            {total === 0 ? "0 of 0" : `${currentIndex + 1} of ${total}`}
          </Text>
        </View>

        <Pressable onPress={onResults} style={styles.bottomCenterBtn}>
          <Text style={styles.bottomBtnText}>Results</Text>
        </Pressable>

        <Pressable
          onPress={onNext}
          disabled={isLast}
          style={[styles.bottomBtn, styles.bottomNextBtn, isLast && styles.bottomBtnDisabled]}
        >
          <Text style={[styles.bottomBtnText, isLast && styles.bottomBtnTextDisabled]}>
            Next
          </Text>
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  pressed: {
    opacity: 0.8,
  },

  disabled: {
    opacity: 0.4,
  },

  disabledText: {
    color: "#9ca3af",
  },

  topWrap: {
    minHeight: 38,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    flexWrap: "wrap",
    rowGap: 8,
    columnGap: 18,
  },

  topGroupLeft: {
    flexDirection: "row",
    alignItems: "center",
    columnGap: 8,
  },

  topGroupRight: {
    flexDirection: "row",
    alignItems: "center",
    columnGap: 14,
    marginLeft: 8,
  },

  topBtn: {
    minWidth: 62,
    height: 34,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    backgroundColor: "#ffffff",
  },

  topNextBtn: {
    minWidth: 68,
  },

  topResultsBtn: {
    minWidth: 82,
    height: 34,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 14,
    borderWidth: 1,
    borderColor: "#dbeafe",
    backgroundColor: "#eff6ff",
  },

  topStatusBox: {
    minWidth: 84,
    height: 34,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 10,
    backgroundColor: "#f8fafc",
    borderWidth: 1,
    borderColor: "#e5e7eb",
  },

  topStatusText: {
    color: "#111827",
    fontSize: 13,
    fontWeight: "800",
  },

  topBtnText: {
    color: "#111827",
    fontSize: 13,
    fontWeight: "800",
  },

  topResultsText: {
    color: "#1d4ed8",
    fontSize: 13,
    fontWeight: "900",
  },

  bottomWrap: {
    position: "absolute",
    left: 0,
    right: 0,
    paddingHorizontal: 12,
    paddingTop: 6,
    backgroundColor: "transparent",
  },

  bottomBar: {
    minHeight: 52,
    borderRadius: 14,
    backgroundColor: "#0B132B",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 8,
    paddingVertical: 6,
    shadowColor: "#000",
    shadowOpacity: 0.14,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 2 },
    elevation: 6,
  },

  bottomBtn: {
    minWidth: 58,
    height: 38,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 8,
  },

  bottomNextBtn: {
    marginLeft: 10,
  },

  bottomCenterBtn: {
    minWidth: 74,
    height: 38,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 10,
    backgroundColor: "#1C2541",
    marginLeft: 6,
    marginRight: 18,
  },

  bottomBtnDisabled: {
    opacity: 0.35,
  },

  bottomBtnText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "700",
  },

  bottomBtnTextDisabled: {
    color: "#D1D5DB",
  },

  bottomStatusBox: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 6,
  },

  bottomStatusText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "700",
  },
});